// src/shared/lib/clinic-time.ts
//
// Clinical times on the CLINIC'S clock, in the design desk's D-45 format ("5 Oct 2026, 22:00"), shared
// by every surface that shows the same fact — the note's chart pieces and their value cards
// (encounter/lib/note-pieces.ts) and the chart's Labs tab — so one result reads one date everywhere.
//
//   · a string WITH a zone (`Z`, `+01:00`, `+0100`) is an instant: shown at clinic local time;
//   · a string with NO zone is already a wall-clock time and keeps its digits;
//   · a bare date keeps its date.
// The clinic zone is FIXED, not the reader's browser zone: a note piece's text is its checksum and must
// be the same on every client. A source that sends UTC without a zone must mark it before it gets here
// (the lab results do: `patients/lib/lab-results.ts` `asUtcInstant`).
import { formatDateD45, formatDateTimeD45 } from './date-format';

/**
 * The clinic's time zone. Chart values are shown in clinic local time (Templates lane, 5 Oct 2026):
 * every Scrips clinic is in the UAE, one zone with no daylight saving. A FIXED zone — not the reader's
 * browser zone — because a piece's text is its checksum and must be the same on every client.
 */
export const CLINIC_TIME_ZONE = 'Asia/Dubai';

/**
 * D-45 (design desk ruling, 5 Oct 2026): chart values read "5 Oct 2026, 22:00" — day month year,
 * 24-hour — in tile headers and in the inserted text alike, through the app's D-45 helpers
 * (`formatDateTimeD45` / `formatDateD45`, src/shared/lib/date-format.ts, from PR #611).
 *
 * WHICH CLOCK. A string WITH a zone (`Z`, `+01:00`) is an instant: it is shown at clinic local time
 * (22:00Z → "6 Oct 2026, 02:00") — what #611's helper shows for that instant in a clinic browser, but
 * computed in `CLINIC_TIME_ZONE` so no client's own zone changes the text. A string with NO zone is
 * already a wall-clock time (a pull stamp, a vital the app wrote on the clinic's clock) and keeps its
 * digits. A bare date keeps its date. A source that sends UTC WITHOUT a zone must say so before it
 * gets here (the lab results do: `patients/lib/lab-results.ts` `asUtcInstant`).
 */
export function clinicDateTimeD45(iso: string | null | undefined): string | undefined {
  const w = asWallClock(iso);
  if (!w) return undefined;
  return (w.timed ? formatDateTimeD45(w.stamp) : formatDateD45(w.stamp)) || undefined;
}

/** The D-45 date alone ("12 Sep 2026"), for a collection or measurement date — on the clinic's calendar. */
export function clinicDateD45(iso: string | null | undefined): string | undefined {
  const w = asWallClock(iso);
  return w ? formatDateD45(w.stamp) || undefined : undefined;
}

const ISO_PARTS = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(Z|[+-]\d{2}:?\d{2})?)?$/i;

/** A clinic wall-clock stamp (no zone) for an ISO string, or undefined when it is not a real date/time. */
function asWallClock(iso: string | null | undefined): { stamp: string; timed: boolean } | undefined {
  const m = ISO_PARTS.exec((iso ?? '').trim());
  if (!m) return undefined;
  const [, y, mo, d, hh, mm, ss, zone] = m;
  // Only a real calendar date and clock time: the helpers use Date, which would quietly roll an
  // impossible "2026-02-31" or "24:00" into another day — a different clinical date (Codex review).
  const month = Number(mo);
  if (month < 1 || month > 12) return undefined;
  const daysInMonth = new Date(Date.UTC(Number(y), month, 0)).getUTCDate();
  if (Number(d) < 1 || Number(d) > daysInMonth) return undefined;
  const timed = hh != null && mm != null;
  if (timed && (Number(hh) > 23 || Number(mm) > 59)) return undefined;
  if (timed && zone) return clinicClock(instantOf(y, mo, d, hh, mm, ss, zone));
  return { stamp: `${y}-${mo}-${d}T${timed ? `${hh}:${mm}` : '00:00'}:00`, timed };
}

/**
 * The instant an ISO string with a zone names, in ms — computed from its parts, never `Date.parse`,
 * whose reading of a colon-less offset (`+0100`) differs between engines (Codex review): the text must
 * be the same on every client.
 */
function instantOf(y: string, mo: string, d: string, hh: string, mm: string, ss: string | undefined, zone: string): number {
  const local = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(hh), Number(mm), Number(ss ?? 0));
  if (/^z$/i.test(zone)) return local;
  const z = /^([+-])(\d{2}):?(\d{2})$/.exec(zone);
  if (!z) return Number.NaN;
  const minutes = (Number(z[2]) * 60 + Number(z[3])) * (z[1] === '-' ? -1 : 1);
  return local - minutes * 60_000;
}

/** The instant (ms) an ISO date-time WITH a zone names; undefined for anything else. Portable. */
export function zonedInstant(iso: string | null | undefined): number | undefined {
  const m = ISO_PARTS.exec((iso ?? '').trim());
  if (!m || m[4] == null || !m[7]) return undefined;
  const t = instantOf(m[1], m[2], m[3], m[4], m[5], m[6], m[7]);
  return Number.isNaN(t) ? undefined : t;
}

/** An instant as the clinic's wall clock. */
function clinicClock(instant: number): { stamp: string; timed: boolean } | undefined {
  if (Number.isNaN(instant)) return undefined;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: CLINIC_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(instant));
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return { stamp: `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}:00`, timed: true };
}

/**
 * PROD-3065a: an instant as Flutter writes a record time — `DateTime.now().toString()` on the clinic's clock,
 * `yyyy-MM-dd HH:mm:ss.SSS`, with no `Z` and no offset (Flutter's FallAssessmentData / PainAssessmentData
 * `createdOn`, fall_assessment_data.dart:138; shown unconverted, fall_assessment_widget.dart:89 and
 * date_time_utils.dart:50). A UTC `toISOString()` there reads four hours early in a Dubai clinic. On
 * `CLINIC_TIME_ZONE`, the app's one clinic clock (above) — not the browser's zone, so every client writes the same
 * wall-clock digits for the same instant.
 */
export function clinicWallClockStamp(instant: Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: CLINIC_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  // Milliseconds are the same in every zone (no zone is offset by a fraction of a second).
  const ms = String(instant.getUTCMilliseconds()).padStart(3, '0');
  return `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}:${part('second')}.${ms}`;
}
